import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CatalogDraft,
  CatalogPaletteType,
  CatalogProduct,
  CatalogTemplate,
  ValidateResponse,
} from '@core/interfaces/catalog.interface';
import { CatalogsService } from '@core/services/catalogs/catalogs.service';
import { finalize } from 'rxjs';
import Swal from 'sweetalert2';
import { CatalogPreviewDialogComponent } from '../../components/catalog-preview-dialog/catalog-preview-dialog.component';
import {
  ProductDialogData,
  ProductFormDialogComponent,
} from '../../components/product-form-dialog/product-form-dialog.component';

const MAX_PRODUCTS = 20;

@Component({
  selector: 'app-create-edit-catalog',
  templateUrl: './create-edit-catalog.component.html',
  styleUrls: ['./create-edit-catalog.component.scss'],
})
export class CreateEditCatalogComponent implements OnInit {
  public draft: CatalogDraft | null = null;
  public draftId = '';
  public phone = '';
  public loading = true;
  public savingDesign = false;
  public savingBusiness = false;
  public uploadingLogo = false;
  public generating = false;

  public readonly maxProducts = MAX_PRODUCTS;

  public templates: { value: CatalogTemplate; label: string }[] = [
    { value: 'elegant', label: 'Elegante' },
    { value: 'modern', label: 'Moderno' },
    { value: 'visual', label: 'Visual' },
  ];

  public design = {
    template: 'elegant' as CatalogTemplate,
    palette_type: 'pastel' as CatalogPaletteType,
    color_primary: '#9a7060',
  };

  public businessForm = this.fb.group({
    name: [''],
    phone: [''],
    address: [''],
    instagram: [''],
    facebook: [''],
  });
  public logo: string | null = null;

  public validation: ValidateResponse | null = null;

  public productColumns = ['image', 'name', 'price', 'description', 'action'];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    public catalogsService: CatalogsService,
  ) {}

  ngOnInit(): void {
    this.phone = this.route.snapshot.queryParamMap.get('phone') || '';
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.draftId = id;
      this.loadDraft();
    } else {
      this.createDraft();
    }
  }

  private createDraft(): void {
    if (!this.phone) {
      this.loading = false;
      Swal.fire(
        'Falta el teléfono',
        'Vuelve al listado y busca un número antes de crear un catálogo.',
        'warning',
      ).then(() => this.router.navigate(['/catalogs/list']));
      return;
    }

    this.catalogsService
      .createDraft({
        phone: this.phone,
        template: this.design.template,
        palette_type: this.design.palette_type,
        color_primary: this.design.color_primary,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (resp) => {
          this.draftId = resp.draft.draft_id;
          this.applyDraft(resp.draft);
          this.router.navigate(['/catalogs/edit', this.draftId], {
            queryParams: { phone: this.phone },
            replaceUrl: true,
          });
        },
        error: () =>
          Swal.fire('Error', 'No se pudo crear el catálogo.', 'error'),
      });
  }

  private loadDraft(): void {
    this.loading = true;
    this.catalogsService
      .getDraft(this.draftId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (resp) => this.applyDraft(resp.draft),
        error: () => {
          Swal.fire('Error', 'No se pudo cargar el catálogo.', 'error').then(
            () => this.router.navigate(['/catalogs/list']),
          );
        },
      });
  }

  private applyDraft(draft: CatalogDraft): void {
    this.draft = draft;
    if (!this.phone) this.phone = draft.phone || '';
    this.design = {
      template: draft.template || 'elegant',
      palette_type: draft.palette_type || 'pastel',
      color_primary:
        draft.color_primary || draft.color_primary_pastel || '#9a7060',
    };
    this.logo = draft.logo || null;
    this.businessForm.patchValue({
      name: draft.business_info?.name || '',
      phone: draft.business_info?.phone || '',
      address: draft.business_info?.address || '',
      instagram: draft.business_info?.instagram || '',
      facebook: draft.business_info?.facebook || '',
    });
    this.validation = null;
  }

  get products(): CatalogProduct[] {
    return this.draft?.products || [];
  }

  get canAddProduct(): boolean {
    return this.products.length < MAX_PRODUCTS;
  }

  // ─────────────── Diseño ───────────────

  saveDesign(): void {
    if (!this.draftId) return;
    this.savingDesign = true;
    this.catalogsService
      .updateDraft(this.draftId, {
        template: this.design.template,
        palette_type: this.design.palette_type,
        color_primary: this.design.color_primary,
      })
      .pipe(finalize(() => (this.savingDesign = false)))
      .subscribe({
        next: (resp) => {
          this.applyDraft(resp.draft);
          this.toast('Diseño guardado');
        },
        error: () =>
          Swal.fire('Error', 'No se pudo guardar el diseño.', 'error'),
      });
  }

  // ─────────────── Negocio ───────────────

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    input.value = '';

    this.uploadingLogo = true;
    this.catalogsService
      .uploadImage(file, 'catalogs/logos')
      .pipe(finalize(() => (this.uploadingLogo = false)))
      .subscribe({
        next: (url) => (this.logo = url),
        error: () => Swal.fire('Error', 'No se pudo subir el logo.', 'error'),
      });
  }

  removeLogo(): void {
    this.logo = null;
  }

  saveBusiness(): void {
    if (!this.draftId) return;
    const v = this.businessForm.getRawValue();
    this.savingBusiness = true;
    this.catalogsService
      .updateDraft(this.draftId, {
        logo: this.logo,
        business_info: {
          name: v.name || '',
          phone: v.phone || '',
          address: v.address || '',
          instagram: v.instagram || '',
          facebook: v.facebook || '',
        },
      })
      .pipe(finalize(() => (this.savingBusiness = false)))
      .subscribe({
        next: (resp) => {
          this.applyDraft(resp.draft);
          this.toast('Datos del negocio guardados');
        },
        error: () =>
          Swal.fire('Error', 'No se pudieron guardar los datos.', 'error'),
      });
  }

  // ─────────────── Productos ───────────────

  addProduct(): void {
    if (!this.canAddProduct) return;
    this.openProductDialog(null);
  }

  editProduct(index: number): void {
    this.openProductDialog(index);
  }

  private openProductDialog(index: number | null): void {
    const data: ProductDialogData = {
      draftId: this.draftId,
      index,
      product: index !== null ? this.products[index] : undefined,
    };
    this.dialog
      .open(ProductFormDialogComponent, { data, width: '600px' })
      .afterClosed()
      .subscribe((updated: CatalogDraft | undefined) => {
        if (updated) this.applyDraft(updated);
      });
  }

  deleteProduct(index: number): void {
    Swal.fire({
      title: '¿Eliminar producto?',
      text: this.products[index]?.name || '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#262D48',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then((r) => {
      if (!r.isConfirmed) return;
      this.catalogsService.deleteProduct(this.draftId, index).subscribe({
        next: (resp) => this.applyDraft(resp.draft),
        error: () =>
          Swal.fire('Error', 'No se pudo eliminar el producto.', 'error'),
      });
    });
  }

  // ─────────────── Validar / Preview / Generar ───────────────

  validate(): void {
    if (!this.draftId) return;
    this.catalogsService.validateDraft(this.draftId).subscribe({
      next: (resp) => (this.validation = resp),
      error: () => Swal.fire('Error', 'No se pudo validar.', 'error'),
    });
  }

  preview(): void {
    this.dialog.open(CatalogPreviewDialogComponent, {
      data: { draftId: this.draftId },
      maxWidth: '760px',
      width: '90vw',
    });
  }

  generate(): void {
    if (!this.draftId || this.generating) return;
    if (this.products.length === 0) {
      Swal.fire('Aviso', 'Agrega al menos un producto.', 'warning');
      return;
    }

    this.generating = true;
    this.catalogsService.generateCatalog(this.draftId, this.phone).subscribe({
      next: () => this.pollGeneration(),
      error: () => {
        this.generating = false;
        Swal.fire('Error', 'No se pudo encolar la generación.', 'error');
      },
    });
  }

  private pollGeneration(): void {
    this.catalogsService.pollDraftUntilDone(this.draftId).subscribe({
      next: (draft) => {
        this.draft = draft;
        if (draft.status === 'completed') {
          this.generating = false;
          this.applyDraft(draft);
          Swal.fire({
            icon: 'success',
            title: 'Catálogo generado',
            confirmButtonText: 'Descargar PDF',
            showCancelButton: true,
            cancelButtonText: 'Cerrar',
          }).then((r) => {
            if (r.isConfirmed && draft.catalog_pdf_url) {
              window.open(draft.catalog_pdf_url, '_blank');
            }
          });
        } else if (draft.status === 'failed') {
          this.generating = false;
          Swal.fire('Error', 'La generación del catálogo falló.', 'error');
        }
      },
      complete: () => {
        if (this.generating) {
          this.generating = false;
          Swal.fire(
            'Sigue procesando',
            'El catálogo aún se está generando. Revisa el listado en unos minutos.',
            'info',
          );
        }
      },
      error: () => {
        this.generating = false;
        Swal.fire('Error', 'No se pudo verificar el estado.', 'error');
      },
    });
  }

  openPdf(url: string): void {
    if (url) window.open(url, '_blank');
  }

  back(): void {
    this.router.navigate(['/catalogs/list']);
  }

  private toast(title: string): void {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title,
      showConfirmButton: false,
      timer: 1800,
    });
  }
}
